<template>
  <div>
    <div
      class="SB-lightbox-trigger"
      :class="{'show-play-overlay': type === 'video'}"
      @click="show"/>
    <div
      :class="{closed}"
      class="SB-lightbox-container">
      <div
        class="SB-lightbox-actions">
        <SBIcon
          class="button button-close"
          :icon="SBIconList.LIGHTBOX_CLOSE"
          color="white"
          :size="18"
          :hover-color="SBColors.blackPrimary"
          :hover-size="40"
          @click="hide"/>
        <SBIcon
          class="button button-download"
          :icon="SBIconList.LIGHTBOX_DOWNLOAD"
          color="white"
          :size="24"
          :hover-color="SBColors.blackPrimary"
          :hover-size="40"
          @click="download"/>
      </div>
      <div
        class="SB-lightbox-content"
        @click="hide" >
        <img
          v-if="type === 'image'"
          :src="source"
          class="SB-lightbox-item"
          @load="imageLoaded"
          @click.stop>
        <video
          v-if="type === 'video'"
          ref="videoPlayer"
          class="SB-lightbox-item SB-lightbox-modal-video"
          controls
          autoplay
          :src="source"
          :type="mime"
          @playing="playing"
          @click.stop>
          Your browser does not support the video tag.
        </video>
      </div>
    </div>
  </div>
</template>

<script>
import Axios from 'axios';
import randomString from '@/helpers/randomString';

import SBIcon from '@/components/common/Icon';
import SBIconList from '@/helpers/lists/sb-icon-list';
import SBColors from '@/helpers/lists/colors';
import { getMediaName, getFileExtension } from '@/helpers/mediaHelpers';

export default {
  name: 'LightBox',
  components: {
    SBIcon,
  },
  props: {
    type: {
      type: String,
      default: '',
    },
    mime: {
      type: String,
      default: '',
    },
    source: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      closed: true,
      isPlaying: false,
      SBIconList,
      SBColors,
    };
  },
  mounted() {
    window.addEventListener('keydown', this.handleKeyDown);
  },
  methods: {
    download() {
      Axios({
        url: `${this.source}?nocache=${randomString()}`, // chrome has a bug when getting image from cache so we need to trick him - https://www.hacksoft.io/blog/handle-images-cors-error-in-chrome
        method: 'GET',
        responseType: 'blob',
      })
        .then((response) => {
          const url = window.URL.createObjectURL(new Blob([response.data]));
          const link = document.createElement('a');
          link.href = url;
          const mediaName = getMediaName(this.source) === getFileExtension(getMediaName(this.source)) ?
            this.type === 'image' ? `${getMediaName(this.source)}.jpg` : this.type === 'video' ? `${getMediaName(this.source)}.mp4` : getMediaName(this.source) :
            getMediaName(this.source);
          link.setAttribute('download', mediaName);
          document.body.appendChild(link);
          link.click();
        });
    },
    show() {
      this.closed = false;
      if (this.type === 'video') {
        this.$refs.videoPlayer.play();
      }
    },
    playing() {
      this.isPlaying = true;
      if (this.closed) {
        this.pauseVideo();
      }
    },
    pauseVideo() {
      this.$refs.videoPlayer.pause();
      this.isPlaying = false;
    },
    handleKeyDown(event) {
      switch (event.keyCode) {
        case 27: // ESCAPE
          this.hide();
          break;
        default:
          break;
      }
    },
    hide() {
      if (this.$refs.videoPlayer && this.isPlaying) {
        // Pausing a video that is started causes error if the video is still loading before the content actually starts playing.
        this.pauseVideo();
      }
      this.closed = true;
    },
    imageLoaded() {
      this.$emit('imageLoaded');
    },
  },
};
</script>

<style lang="scss" scoped>
.SB-lightbox-trigger {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 50;
  cursor: pointer;
  opacity: 0;
}

.SB-lightbox-container {
  position: fixed;
  align-items: center;
  background-color: rgba(0, 0, 0, 0.95);
  box-sizing: border-box;
  display: -moz-box;
  display: -ms-flexbox;
  display: -webkit-box;
  display: -webkit-flex;
  display: flex;
  height: 100%;
  justify-content: center;
  left: 0;
  padding: 10px;
  top: 0;
  width: 100%;
  z-index: 2000;
  -webkit-align-items: center;
  -moz-box-sizing: border-box;
  -webkit-justify-content: center;
  -ms-flex-align: center;
  -webkit-box-align: center;
  -ms-flex-pack: center;
  -webkit-box-pack: center;

  &.closed {
    display: none !important;
  }
}

.SB-lightbox-content {
  position: relative;
}

.SB-lightbox-trigger.show-play-overlay {
  background-image: url('~@/assets/img/video-overlay.svg');
  background-size: 72px;
  background-repeat: no-repeat;
  background-position: center;
  background-color: rgba(red($blackPrimary), green($blackPrimary), blue($blackPrimary), 0.3);
  opacity: 1;
}

.SB-lightbox-container {
  background-color: rgba(red($blackPrimary), green($blackPrimary), blue($blackPrimary), 0.92);

  .SB-lightbox-actions {
    display: flex;
    align-items: center;
    flex-direction: row-reverse;
    position: absolute;
    top: 0;
    right: 0;
    left: 0;
    height: 73px;
    border-bottom: 1px solid #53595d;

    .button {
      margin: 0 17px 0 0;
      cursor: pointer;
    }

    .button-close {

    }
  }

  .SB-lightbox-content {
    display: flex;
    align-items: center;
    justify-content: center;
    position: absolute;
    top: 74px;
    bottom: 0;
    right: 0;
    left: 0;

    .SB-lightbox-item {
      max-height: calc(100vh - 188px);
      height: auto;
      margin: 0 auto;
      max-width: calc(100% - 116px);
      min-width: 100px;
      user-select: none;
      -webkit-user-select: none;
      -moz-user-select: none;
      -ms-user-select: none;
    }
  }
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity .2s ease;
}

.fade-enter, .fade-leave-to {
  opacity: 0;
}

.no-scroll {
  overflow-y: hidden;
}
</style>
